import { QueryClient, useMutation, useQueryClient, useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { axiosInstance } from '@/api/axios'
import {
  GETOrderTypesResponse,
  PATCHOrderTypeRequest,
  PATCHOrderTypeResponse,
  GETOrderSummaryResponse,
  OrderCompositionPostRequest,
  OrderCompositionPatchRequest,
  OrderDataPostRequest,
  OrderDataPatchRequest,
  TotalOrderDataResponse,
  SwitchOrderInputTypeRequest
} from './types'
import {
  concatQueryKey,
  createQueryKey,
  FINANCIAL_MODEL_SETTINGS_BASE_KEY,
  REVENUES_BASE_KEY,
  TOTALS_KEY,
  resetGlobalQueryKey
} from '@/api/query-keys'
import { PeriodType, PeriodTypeQuery, PeriodTypeView } from '@/types'
import { CUSTOMER_SEGMENTS_KEY } from './customer-segments'

export const ORDER_TYPES_KEY = createQueryKey({
  baseKey: REVENUES_BASE_KEY,
  keys: [`orderTypes`]
})

export const ORDERS_SUMMARY_KEY = createQueryKey({
  baseKey: REVENUES_BASE_KEY,
  keys: [`orders-summary`]
})

export const ORDERS_TOTALS_KEY = createQueryKey({
  baseKey: REVENUES_BASE_KEY,
  keys: [`orders-totals`]
})

export const getOrderTypes = async (financialModelId: string) => {
  const response = await axiosInstance.get<GETOrderTypesResponse>(`/financial-models/${financialModelId}/order-types`)
  return response.data
}

export const useGetOrderTypesQuery = (financialModelId: string) =>
  useSuspenseQuery({
    queryKey: ORDER_TYPES_KEY.concat([financialModelId]),
    queryFn: () => getOrderTypes(financialModelId)
  })

export const patchOrderType = async (financialModelId: string, id: string, orderType: PATCHOrderTypeRequest): Promise<PATCHOrderTypeResponse> => {
  const response = await axiosInstance.patch<PATCHOrderTypeResponse>(`/financial-models/${financialModelId}/order-types/${id}`, orderType)
  return response.data
}

export const useUpdateOrderTypeMutation = (financialModelId: string, id: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (orderType: PATCHOrderTypeRequest) => patchOrderType(financialModelId, id, orderType),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ORDER_TYPES_KEY.concat([financialModelId])
      })
      queryClient.invalidateQueries({
        queryKey: CUSTOMER_SEGMENTS_KEY.concat([financialModelId])
      })
      queryClient.invalidateQueries({
        queryKey: ORDERS_SUMMARY_KEY.concat([financialModelId])
      })
      queryClient.invalidateQueries({
        queryKey: ORDERS_TOTALS_KEY.concat([financialModelId])
      })
      queryClient.invalidateQueries({
        queryKey: FINANCIAL_MODEL_SETTINGS_BASE_KEY
      })
      resetGlobalQueryKey([TOTALS_KEY])
    }
  })
}

export const getOrderSummary = async (financialModelId: string, period: PeriodTypeQuery, view?: PeriodTypeView) => {
  const response = await axiosInstance.get<GETOrderSummaryResponse>(`/financial-models/${financialModelId}/orders-summary`, {
    params: {
      type: period,
      view: view ?? `monthly`
    }
  })

  return response.data
}

export const useGetOrderSummarySuspenseQuery = (financialModelId: string, period: PeriodTypeQuery, view?: PeriodTypeView) =>
  useSuspenseQuery({
    queryKey: concatQueryKey(ORDERS_SUMMARY_KEY, [financialModelId, period, view ?? `monthly`]),
    queryFn: () => getOrderSummary(financialModelId, period, view)
  })

export const useHistoricalOrderSummary = ({ financialModelId, view }: { financialModelId?: string; view?: PeriodTypeView }) =>
  useQuery({
    queryKey: concatQueryKey(ORDERS_SUMMARY_KEY, [financialModelId ?? ``, `historical`, view ?? `monthly`]),
    queryFn: () => getOrderSummary(financialModelId!, `historical`, view),
    enabled: Boolean(financialModelId)
  })

export const postOrderComposition = async (financialModelId: string, orderComposition: OrderCompositionPostRequest) => {
  const response = await axiosInstance.post(`/financial-models/${financialModelId}/order-composition/`, orderComposition)

  return response.data
}

export const postOrderCompositionBulk = async (financialModelId: string, orderCompositions: OrderCompositionPostRequest[]) => {
  const response = await axiosInstance.post(`/financial-models/${financialModelId}/order-composition/bulk/`, {
    periods: orderCompositions
  })

  return response.data
}

export const getCreateOrderCompositionMutationArgs = (financialModelId: string, queryClient: QueryClient) => ({
  mutationFn: (orderComposition: OrderCompositionPostRequest) => postOrderComposition(financialModelId, orderComposition),
  onSuccess: () => {
    queryClient.invalidateQueries({
      queryKey: ORDERS_SUMMARY_KEY.concat([financialModelId])
    })
    queryClient.invalidateQueries({
      queryKey: ORDERS_TOTALS_KEY.concat([financialModelId])
    })
    resetGlobalQueryKey([TOTALS_KEY])
  },
  mutationKey: [`orders-summary`]
})

export const getBulkCreateOrderCompositionMutationArgs = (financialModelId: string) => ({
  mutationFn: (orderComposition: OrderCompositionPostRequest) => postOrderComposition(financialModelId, orderComposition)
})

export const useCreateOrderCompositionMutation = (financialModelId: string) => {
  const queryClient = useQueryClient()

  return useMutation(getCreateOrderCompositionMutationArgs(financialModelId, queryClient))
}

export const patchOrderComposition = async (financialModelId: string, order_id: string, orderComposition: OrderCompositionPatchRequest) => {
  const response = await axiosInstance.patch<OrderCompositionPatchRequest>(
    `/financial-models/${financialModelId}/order-composition/${order_id}`,
    orderComposition
  )

  return response.data
}

export const getPatchOrderCompositionMutationArgs = (financialModelId: string, orderId: string, queryClient: QueryClient) => ({
  mutationFn: (orderComposition: OrderCompositionPatchRequest) => patchOrderComposition(financialModelId, orderId, orderComposition),
  onSuccess: () => {
    queryClient.invalidateQueries({
      queryKey: ORDERS_SUMMARY_KEY.concat([financialModelId])
    })
    queryClient.invalidateQueries({
      queryKey: ORDERS_TOTALS_KEY.concat([financialModelId])
    })
    resetGlobalQueryKey([TOTALS_KEY])
  },
  mutationKey: [`orders-summary`]
})

export const getBulkPatchOrderCompositionMutationArgs = (financialModelId: string, orderId: string) => ({
  mutationFn: (orderComposition: OrderCompositionPatchRequest) => patchOrderComposition(financialModelId, orderId, orderComposition)
})

export const useUpdateOrderCompositionMutation = (financialModelId: string, order_id: string) => {
  const queryClient = useQueryClient()

  return useMutation(getPatchOrderCompositionMutationArgs(financialModelId, order_id, queryClient))
}

export const postOrderData = async (financialModelId: string, orderData: OrderDataPostRequest) => {
  const response = await axiosInstance.post(`/financial-models/${financialModelId}/order-data`, orderData)

  return response.data
}

export const postOrderDataBulk = async (financialModelId: string, orderData: OrderDataPostRequest[]) => {
  const response = await axiosInstance.post(`/financial-models/${financialModelId}/order-data/bulk/`, { periods: orderData })

  return response.data
}

export const getCreateOrderDataMutationArgs = (financialModelId: string, queryClient: QueryClient) => ({
  mutationFn: (orderData: OrderDataPostRequest) => postOrderData(financialModelId, orderData),
  onSuccess: () => {
    queryClient.invalidateQueries({
      queryKey: ORDERS_SUMMARY_KEY.concat([financialModelId])
    })
    queryClient.invalidateQueries({
      queryKey: ORDERS_TOTALS_KEY.concat([financialModelId])
    })
    resetGlobalQueryKey([TOTALS_KEY])
  },
  mutationKey: [`orders-summary`]
})

export const getBulkCreateOrderDataMutationArgs = (financialModelId: string) => ({
  mutationFn: (orderData: OrderDataPostRequest) => postOrderData(financialModelId, orderData)
})

export const useCreateOrderDataMutation = (financialModelId: string) => {
  const queryClient = useQueryClient()

  return useMutation(getCreateOrderDataMutationArgs(financialModelId, queryClient))
}

export const patchOrderData = async (financialModelId: string, order_id: string, orderData: OrderDataPatchRequest) => {
  const response = await axiosInstance.patch<OrderDataPatchRequest>(`/financial-models/${financialModelId}/order-data/${order_id}`, orderData)

  return response.data
}

export const getUpdateOrderDataMutationArgs = (financialModelId: string, orderId: string, queryClient: QueryClient) => ({
  mutationFn: (orderData: OrderDataPatchRequest) => patchOrderData(financialModelId, orderId, orderData),
  onSuccess: () => {
    queryClient.invalidateQueries({
      queryKey: ORDERS_SUMMARY_KEY.concat([financialModelId])
    })
    queryClient.invalidateQueries({
      queryKey: ORDERS_TOTALS_KEY.concat([financialModelId])
    })
    resetGlobalQueryKey([TOTALS_KEY])
  },
  mutationKey: [`orders-summary`]
})

export const getBulkUpdateOrderDataMutationArgs = (financialModelId: string, orderId: string) => ({
  mutationFn: (orderData: OrderDataPatchRequest) => patchOrderData(financialModelId, orderId, orderData)
})

export const useUpdateOrderDataMutation = (financialModelId: string, order_id: string) => {
  const queryClient = useQueryClient()

  return useMutation(getUpdateOrderDataMutationArgs(financialModelId, order_id, queryClient))
}

export const switchOrderInputType = async (financialModelId: string, inputType: SwitchOrderInputTypeRequest) => {
  const response = await axiosInstance.post(
    `/financial-models/${financialModelId}/order/${inputType.customer_segment_id}/config/${inputType.column_name}`,
    {},
    {
      params: {
        input_type: inputType.input_type,
        product_id: inputType.product_id,
        year: inputType.year,
        month: inputType.month
      }
    }
  )

  return response.data
}

export const useSwitchOrderInputTypeMutation = (financialModelId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (inputType: SwitchOrderInputTypeRequest) => switchOrderInputType(financialModelId, inputType),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ORDERS_SUMMARY_KEY.concat([financialModelId])
      })
      queryClient.invalidateQueries({
        queryKey: ORDERS_TOTALS_KEY.concat([financialModelId])
      })
      resetGlobalQueryKey([TOTALS_KEY])
    }
  })
}

export const getTotalOrders = async (financialModelId: string, period: PeriodType, view?: PeriodTypeView) => {
  const response = await axiosInstance.get<TotalOrderDataResponse>(`/financial-models/${financialModelId}/orders-totals`, {
    params: {
      type: period,
      view: view ?? `monthly`
    }
  })

  return response.data
}

export const useGetTotalOrdersSuspenseQuery = (financialModelId: string, period: PeriodType, view?: PeriodTypeView) =>
  useSuspenseQuery({
    queryKey: ORDERS_TOTALS_KEY.concat([financialModelId, period, view ?? `monthly`]),
    queryFn: () => getTotalOrders(financialModelId, period, view)
  })
